import type { CreativeTemplateSocialFields } from '@xmiles/holopix-api';
﻿/** 前端 UI：列表主 Tab 语义，出站前映射为 filterType 1|2|3 */
export type CreativeTemplateListScope = 'all' | 'favorite' | 'recent' | 'community' | 'mine'

/** 前端 UI：排序语义，出站前映射为 showType 1|2|3；我的模板专用排序在 my-templates/constants.ts 定义。 */
export type CreativeTemplateSortType = 'latest' | 'hot' | 'used' | 'reward'

/** 服务端 filterType：1 官方模板 2 我的收藏 3 最近使用 4 社区模板 5 我的模板。 */
export type CreativeTemplateFilterType = 1 | 2 | 3 | 4 | 5

/** 服务端 showType：1 推荐 2 最新 3 最热 4 使用最多 5 奖励最多。 */
export type CreativeTemplateShowType = 1 | 2 | 3 | 4 | 5

/** 我的模板状态：0 已发布、1 已下架、2 草稿、3 审核中、4 审核不通过。 */
export type CreativeTemplateStatus = 0 | 1 | 2 | 3 | 4

export type CreativeTemplatePromptSlotType =
  | 'text'
  | 'dropdown-text'
  | 'image-selector'
  | 'video-selector'
  | 'slider'
  | 'radio'
  | 'image-uploader'
  | 'video-uploader'
  | 'hidden'
  | 'switch'

export type CreativeTemplatePromptSlotVipLevel = 'VIP' | 'SVIP' | 'VVIP'

export type CreativeTemplatePromptSlotSelectMode = 'single' | 'multiple'

export interface CreativeTemplatePromptSlotOption {
  id?: string
  label: string
  value?: string | number
  prompt?: string
  src?: string
  /** 封面展示图（图片选择器专用，支持 GIF/MP4/WebM，展示用） */
  cover?: string
  /** 封面类型：image 表示图片/GIF，video 表示 MP4/WebM 视频封面 */
  coverType?: 'image' | 'video'
  cost?: number
  vipLevel?: CreativeTemplatePromptSlotVipLevel
  disabled?: boolean
  default?: boolean
}

/** text 控件无专属 props */
export interface CreativeTemplatePromptSlotTextProps {
  // 对齐后台协议：text 控件 props 为空对象
}

export interface CreativeTemplatePromptSlotDropdownTextProps {
  selectMode?: CreativeTemplatePromptSlotSelectMode
  /** 单选时的默认选中项索引 */
  defaultIndex?: number
  /** 多选时的默认选中项索引 */
  defaultIndexes?: number[]
  options?: CreativeTemplatePromptSlotOption[]
}

export interface CreativeTemplatePromptSlotMediaSelectorProps {
  /** 默认选中项索引 */
  defaultIndex?: number
  options?: CreativeTemplatePromptSlotOption[]
}

/** image-uploader 控件专属 props */
export interface CreativeTemplatePromptSlotImageUploaderProps {
  /** 最大上传数量 */
  maxCount?: number
  /** 单文件最大大小 (MB) */
  maxSize?: number
  /** 图片最小宽度 (px)，0 表示不限制 */
  minWidth?: number
  /** 图片最大宽度 (px)，0 表示不限制 */
  maxWidth?: number
  /** 图片最小高度 (px)，0 表示不限制 */
  minHeight?: number
  /** 图片最大高度 (px)，0 表示不限制 */
  maxHeight?: number
  /** 允许的文件格式 */
  accept?: string[]
  /** 首尾帧模式下的帧角色：空=普通上传，'first'=首帧，'last'=尾帧 */
  frameRole?: '' | 'first' | 'last'
}

/** video-uploader 控件专属 props */
export interface CreativeTemplatePromptSlotVideoUploaderProps {
  /** 最大上传数量 */
  maxCount?: number
  /** 单文件最大大小 (MB) */
  maxSize?: number
  /** 视频最小时长 (秒)，0 表示不限制 */
  minDuration?: number
  /** 视频最大时长 (秒)，0 表示不限制 */
  maxDuration?: number
  /** 视频最小宽度 (px)，0 表示不限制 */
  minWidth?: number
  /** 视频最大宽度 (px)，0 表示不限制 */
  maxWidth?: number
  /** 视频最小高度 (px)，0 表示不限制 */
  minHeight?: number
  /** 视频最大高度 (px)，0 表示不限制 */
  maxHeight?: number
  /** 允许的文件格式 */
  accept?: string[]
}

/** hidden 隐藏控件专属 props：提示词 + 参考图（均可选） */
export interface CreativeTemplatePromptSlotHiddenProps {
  /** 隐藏提示词：{{promptN}} 占位符替换值 */
  prompt?: string
  /** 参考图 URL：{{imageN}} 对应图片，被模板 prompt 引用时追加进生成请求 */
  image?: string
}

export interface CreativeTemplatePromptSlotSliderProps {
  min?: number
  max?: number
  step?: number
  unit?: string
  /** 滑块默认值（后台协议字段名 def） */
  def?: number
}

export interface CreativeTemplatePromptSlotRadioProps {
  /** 默认选中项索引 */
  defaultIndex?: number
  options?: CreativeTemplatePromptSlotOption[]
}

/** switch 开关控件专属 props：控件值恒为布尔 true/false */
export interface CreativeTemplatePromptSlotSwitchProps {
  /** 默认状态（false=关） */
  def?: boolean
  /** 开(true)时占位符注入的文本，空则移除占位符 */
  onValue?: string
  /** 关(false)时占位符注入的文本，空则移除占位符 */
  offValue?: string
}

export type CreativeTemplatePromptSlotProps =
  | CreativeTemplatePromptSlotTextProps
  | CreativeTemplatePromptSlotDropdownTextProps
  | CreativeTemplatePromptSlotMediaSelectorProps
  | CreativeTemplatePromptSlotSliderProps
  | CreativeTemplatePromptSlotRadioProps
  | CreativeTemplatePromptSlotSwitchProps
  | CreativeTemplatePromptSlotImageUploaderProps
  | CreativeTemplatePromptSlotVideoUploaderProps
  | CreativeTemplatePromptSlotHiddenProps

/**
 * 前台共享的 promptSlots DTO：
 * - 顶层只保留渲染/替换/回填共用字段
 * - 类型专属字段统一收敛到 props，避免消费层继续直接处理原始 JSON
 */
export interface CreativeTemplatePromptSlot {
  id: string
  type: CreativeTemplatePromptSlotType
  name: string
  placeholder: string
  required: boolean
  /** 禁止输入：控件仅展示不可编辑（与 required 互斥，后台配置时恒将 required 置 false） */
  forbidInput?: boolean
  cost: number
  vipLevel?: CreativeTemplatePromptSlotVipLevel
  props: CreativeTemplatePromptSlotProps
}

/**
 * 模板控件值集合：文本类控件为字符串，switch 开关控件为原生布尔（true/false 恒有值）。
 * 出站 GeneTaskReq.slotValues 原样携带布尔，供后端 nodeAssociations 回填 ComfyUI 节点 boolean 输入。
 */
export type CreativeTemplateSlotValues = Record<string, string | boolean>

/**
 * parser 输入兼容层：
 * - 兼容旧版纯文本槽位 `{id,name,placeholder}`
 * - 兼容新版 props 分层结构
 */
export interface CreativeTemplatePromptSlotLegacyRaw {
  id?: string | number
  name?: string
  placeholder?: string
}

export interface CreativeTemplatePromptSlotRaw extends CreativeTemplatePromptSlotLegacyRaw {
  type?: string
  required?: boolean | number
  forbidInput?: boolean | number
  cost?: number | string
  vipLevel?: string
  props?: Record<string, unknown>
  options?: unknown
  selectMode?: string
  defaultValue?: unknown
  min?: number | string
  max?: number | string
  step?: number | string
  unit?: string
}

export type CreativeTemplatePromptSlotsInput =
  | string
  | CreativeTemplatePromptSlot[]
  | CreativeTemplatePromptSlotRaw[]

/** 接口可覆盖默认分类色表，TODO 联调 */
export interface CreativeTemplateCategoryThemeOverride {
  background?: string
  color?: string
  titleAccent?: string
}

/** 分类项（列表展示 + 请求 categoryId） */
export interface CreativeTemplateCategoryItem {
  id: number
  label: string
  theme?: CreativeTemplateCategoryThemeOverride
}

/** 服务端分类列表响应项（严格对齐 `接口说明1.md`） */
export interface CreativeTemplateCategoryRespRaw {
  /** 分类id */
  id: number
  /** 分类名称 */
  desc: string
  /** 分类颜色 */
  color: string
  /** 分类编码 */
  code: string
}

/** POST /api/creativeTemplate/collect 请求体（与 `接口文档说明.md` 一致） */
export interface CreativeTemplateCollectReq {
  /** 创意模板 id：**取值**为列表项 `CreativeTemplateResp.id` */
  templateId: number
  /** 收藏 0 否 1 是 */
  collect: 0 | 1
}

/** 创意模板服务端类型：图片、视频、ComfyUI。 */
export type CreativeTemplateType = 1 | 2 | 3

/**
 * 创意模板列表 POST 请求体（与 `接口文档说明.md` 中 CreativeTemplateListReq 一致）
 */
export interface CreativeTemplateListReq {
  /** 筛选：1 全部，2 我的收藏，3 最近使用 */
  filterType?: CreativeTemplateFilterType
  /** 1 推荐，2 最新，3 最热 */
  showType?: CreativeTemplateShowType
  /** 我的模板状态；不传表示全部状态。 */
  status?: CreativeTemplateStatus | null
  /** 创建时间筛选起点，格式 yyyy-MM-dd HH:mm:ss。 */
  startTime?: string
  /** 创建时间筛选终点，格式 yyyy-MM-dd HH:mm:ss。 */
  endTime?: string
  /** 尺寸比例筛选，如 1:1、16:9。 */
  aspectRatio?: string
  /** 名称/搜索 */
  name?: string
  categoryId?: number
  pageNo?: number
  pageSize?: number
  /** 已登录时传；游客不传 */
  userId?: number
  /** 模板类型筛选：1 图片，2 视频，3 ComfyUI */
  typeList?: CreativeTemplateType[]
}

/**
 * 创意模板响应结构（服务端原始 DTO，对齐 `接口说明.md` 的 CreativeTemplateResp）。
 * 注意：`promptSlots/showcaseImages/tags` 在接口文档中类型为 string，但语义上为结构化数组，前端在 UI DTO 层解析后使用。
 */
export interface CreativeTemplateRespRaw extends CreativeTemplateSocialFields {
  /** 模板 ID */
  id: number
  /** 所属团队 ID。 */
  teamId?: number
  /** 模板名称 */
  name: string
  /** 模板描述 */
  description: string
  /** 封面图 */
  preview: string
  /** 模型 */
  model: string
  /** 提示词 */
  prompt: string
  /** 尺寸比例 */
  aspectRatios: string
  /** 模版分类（主分类，兼容旧字段） */
  categoryId?: number
  /** 模版分类 id 列表（详情多标签展示优先；更新/发布请求亦使用此数组） */
  categoryIds?: number[]
  /** 全局排序 */
  globalSort?: number
  /** 图片质量 */
  quality: string
  /** 模板要求上传的参考图数量 */
  referImageCount: number
  /** 是否支持输入提示词：0-不支持，1-支持 */
  input: number
  /** 输出数量 1-8 */
  outputCount?: number
  /** 模板占位提示词槽位配置（接口文档为 string；语义为 JSON 数组） */
  promptSlots?: string
  /** 标签数组（接口文档为 string；语义为数组） */
  tags?: string
  /** 功能说明图（接口文档为 string；语义为 [{image,text}] 或 [{video,text}]） */
  showcaseImages?: string
  /** 类型：1.image,2.video,3.comfyui */
  type?: CreativeTemplateType
  /** 图片类型：0=单图，1=多图，2=PSD，3=视频；ComfyUI 模板据此路由生图模块 */
  picsType?: number
  /** 分类内排序 */
  typeSort?: number
  /** 排序 */
  sort?: number
  /** 副标题 */
  subtitle?: string
  /** 智能强化开关：0-关闭，1-开启 */
  smartPromptEnabled?: number
  /** 智能强化是否透传图片：0-不透传，1-透传 */
  smartPassImages?: number
  /** 智能强化最大 Token（缺省时 chat/completions 不传 maxTokens） */
  maxTokens?: number
  /** 智能强化规则 */
  smartPromptRules?: string
  /** 智能强化模型 */
  smartPromptModel?: string
  /** 锁定字段，逗号分隔（如 "model, aspectRatios, quality"），被锁定的字段不可修改 */
  lockField: string
  /** 删除状态：0-未删除，1-已删除 */
  deleteStatus: number
  /** 状态：0-启用，1-关闭 */
  status: number
  /** 草稿审核态：0 无、1 审核中、2 驳回 */
  auditStatus?: number
  /** 已发布模板的修改稿内容 */
  backupContent?: string
  /** 审核驳回原因 */
  rejectReason?: string
  /** 创建时间 */
  ctime: string
  /** 更新时间 */
  utime: string
  /** 使用量（后端就绪后返回，前端格式化为如 1.2w） */
  usageCount?: number
  /** 是否已收藏：0 否 1 是（与列表/接口文档 `collect` 一致） */
  collect?: number
  /** 预计生成耗时 */
  waitTime?: number
  /** 生图消耗算力 */
  points?: number
  /** 待领取的模板使用奖励算力。 */
  pendingPoints?: number
  /** 已累计领取的模板使用奖励算力。 */
  totalClaimed?: number
  /** 模板扩展参数 JSON（视频模式等），结构如 {"video":{"mode":"start-end"}} */
  param?: string
}

/** 创意模板响应结构（UI DTO：在 Raw 基础上解析字段后给 UI 使用） */
export interface CreativeTemplateResp extends Omit<CreativeTemplateRespRaw, 'promptSlots' | 'tags' | 'showcaseImages' | 'collect'> {
  /** 是否已收藏：0 否 1 是（与接口文档 `collect` 一致） */
  collect?: 0 | 1
  /** 模板占位提示词槽位列表（后端可能为 JSON 字符串/或已解析数组；前端统一解析后使用） */
  promptSlots?: CreativeTemplatePromptSlotsInput
  /** UI 映射层保留的严格校验结果；false 时禁止把已被宽容解析清空的数据应用到草稿。 */
  _promptSlotsValid?: boolean
  /** 标签数组（后端可能为 JSON 字符串/或已解析数组） */
  tags?: string[] | string
  /** 功能说明图（后端可能为 JSON 字符串/或已解析数组） */
  showcaseImages?: unknown
}

/** 列表项是否已收藏（以服务端 `collect` 为准） */
export function isCreativeTemplateListItemCollected(item: CreativeTemplateResp): boolean {
  return item.collect === 1
}

/**
 * 列表项主键 `id`（收藏接口里作为 **`templateId`** 字段的值传出）。
 */
export function getCreativeTemplateCardId(item: CreativeTemplateResp): number {
  return Number(item.id)
}

/** 画布首页模板详情深链 query 名，与 design 约定一致 */
export const CREATIVE_TEMPLATE_DETAIL_QUERY_KEY = 'creativeTemplateId'

/**
 * 详情弹窗单页轮播项：一页仅 **一张** 示意图 + 一段说明（与当前页同步）。
 * 常见为 `slides.length === 1`；多页时为多套「单图+文案」。
 * TODO 联调：与后端字段名对齐（如 `image` / `imageUrl` / `cover`）
 */
export interface CreativeTemplateDetailSlide {
  /** 该页唯一示意图 URL */
  image: string
  /** 该页视频 URL（视频类型轮播项），与 image 二选一 */
  video?: string
  /** 与当前页图片同步展示的说明文案 */
  body: string
}

/**
 * 模板详情（列表字段 + 详情扩展）
 * TODO 联调：subtitle、detailTags、detailCategoryTags、slides 等与后端契约对齐
 */
export interface CreativeTemplateDetail extends CreativeTemplateResp {
  /** 主分类 id（用于标题与主题对齐），TODO 联调 */
  primaryCategoryId?: number
  /** 副标题，TODO 联调 */
  subtitle?: string
  /** 详情页普通标签 pill（与分类标签分字段），TODO 联调字段名 */
  detailTags?: { label: string }[]
  /** 详情页分类标签 pill；categoryId 优先于 label 映射，TODO 联调 */
  detailCategoryTags?: { label: string; categoryId?: number }[]
  /** 图文同步轮播，TODO 联调 */
  slides: CreativeTemplateDetailSlide[]
}

